import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, formatApiError } from '@/lib/AuthContext';
import { Shield, AlertTriangle, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function MfaSetupPage() {
  const navigate = useNavigate();
  const { user, setupMfa, verifyMfa } = useAuth();
  const [step, setStep] = useState('start'); // start, scan, verify
  const [mfaData, setMfaData] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const data = await setupMfa();
      setMfaData(data);
      setStep('scan');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(code);
      navigate(user?.is_platform_admin ? '/admin' : '/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2533] flex items-center justify-center px-4" data-testid="mfa-setup-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-white font-bold text-2xl">Nuvanta Solutions</span>
            <span className="text-[#4DD0E1]">|</span>
            <span className="text-[#4DD0E1] font-semibold text-2xl">TradePath</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="h-5 w-5 text-[#0097A7]" />
            <h1 className="text-xl font-bold text-[#0A2533]">Set up two-factor authentication</h1>
          </div>
          <p className="text-sm text-[#4E6974] mb-6">
            Platform admins must enable MFA. Use an authenticator app (Google Authenticator, Authy, etc.).
          </p>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm rounded-lg p-3 mb-4" data-testid="mfa-error">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" /> <span>{error}</span>
            </div>
          )}

          {step === 'start' && (
            <button data-testid="mfa-start-btn" onClick={handleSetup} disabled={loading}
              className="w-full py-2.5 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white font-medium text-sm rounded-lg transition-colors">
              {loading ? 'Setting up...' : 'Generate QR code'}
            </button>
          )}

          {step === 'scan' && mfaData && (
            <div className="space-y-4">
              <div className="flex justify-center p-4 bg-[#F5F5F5] rounded-lg">
                <QRCodeSVG value={mfaData.otpauth_url} size={200} data-testid="mfa-qr-code" />
              </div>
              <div className="bg-[#F5F5F5] rounded-lg p-3">
                <p className="text-xs text-[#4E6974] mb-1">Can't scan? Enter this key manually:</p>
                <code className="text-xs text-[#0A2533] font-mono break-all" data-testid="mfa-secret">{mfaData.secret}</code>
              </div>
              <button onClick={() => setStep('verify')} data-testid="mfa-next-btn"
                className="w-full py-2.5 bg-[#00707C] hover:bg-[#005F6A] text-white font-medium text-sm rounded-lg transition-colors">
                I've scanned the QR code
              </button>
            </div>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#4E6974] mb-1.5">Enter the 6-digit code from your authenticator app</label>
                <input data-testid="mfa-code-input" type="text" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full text-center text-2xl tracking-[0.5em] font-mono py-3 border border-[#E0E0E0] rounded-lg text-[#0A2533] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none"
                  placeholder="000000" required autoFocus />
              </div>
              <button data-testid="mfa-verify-btn" type="submit" disabled={loading || code.length !== 6}
                className="w-full py-2.5 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white font-medium text-sm rounded-lg transition-colors">
                {loading ? 'Verifying...' : 'Verify and enable MFA'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
